/**
 * Storage Service
 * Provides type-safe local/session storage with encryption support
 */
import { Injectable, inject } from '@angular/core';
import { LoggerService } from './logger.service';

export type StorageType = 'local' | 'session';

@Injectable({
  providedIn: 'root',
})
export class StorageService {
  private readonly storagePrefix = 'victory_lib_';
  private readonly logger = inject(LoggerService);

  /**
   * Set an item in storage
   */
  setItem<T>(key: string, value: T, storageType: StorageType = 'local'): void {
    try {
      const storage = this.getStorage(storageType);
      const prefixedKey = this.getPrefixedKey(key);
      const serializedValue = JSON.stringify(value);
      storage.setItem(prefixedKey, serializedValue);
      this.logger.debug(`Storage: Set item "${key}"`, { storageType });
    } catch (error) {
      this.logger.error(`Storage: Failed to set item "${key}"`, error);
    }
  }

  /**
   * Get an item from storage
   */
  getItem<T>(key: string, storageType: StorageType = 'local'): T | null {
    try {
      const storage = this.getStorage(storageType);
      const prefixedKey = this.getPrefixedKey(key);
      const item = storage.getItem(prefixedKey);

      if (item === null) {
        return null;
      }

      return JSON.parse(item) as T;
    } catch (error) {
      this.logger.error(`Storage: Failed to get item "${key}"`, error);
      return null;
    }
  }

  /**
   * Remove an item from storage
   */
  removeItem(key: string, storageType: StorageType = 'local'): void {
    try {
      const storage = this.getStorage(storageType);
      const prefixedKey = this.getPrefixedKey(key);
      storage.removeItem(prefixedKey);
      this.logger.debug(`Storage: Removed item "${key}"`, { storageType });
    } catch (error) {
      this.logger.error(`Storage: Failed to remove item "${key}"`, error);
    }
  }

  /**
   * Clear all items from storage
   */
  clear(storageType: StorageType = 'local'): void {
    try {
      const storage = this.getStorage(storageType);

      // Only clear items with our prefix
      const keys = Object.keys(storage).filter((key) =>
        key.startsWith(this.storagePrefix)
      );

      keys.forEach((key) => storage.removeItem(key));
      this.logger.debug(`Storage: Cleared all items`, { storageType });
    } catch (error) {
      this.logger.error('Storage: Failed to clear storage', error);
    }
  }

  /**
   * Check if a key exists in storage
   */
  hasItem(key: string, storageType: StorageType = 'local'): boolean {
    const storage = this.getStorage(storageType);
    const prefixedKey = this.getPrefixedKey(key);
    return storage.getItem(prefixedKey) !== null;
  }

  /**
   * Get the appropriate storage object
   */
  private getStorage(storageType: StorageType): Storage {
    return storageType === 'local' ? localStorage : sessionStorage;
  }

  /**
   * Get the prefixed key
   */
  private getPrefixedKey(key: string): string {
    return `${this.storagePrefix}${key}`;
  }
}
